/**
 * Swaps collection product-card gallery images on facet filter changes without
 * re-downloading images that are already in the DOM or browser cache.
 */

const POSITION_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const PREFERRED_FINISH = 'gloss black premium milled';
const PREFERRED_FINISH_HANDLE = 'gloss-black-premium-milled';
const FALLBACK_FINISH = 'polished';
const FALLBACK_FINISH_HANDLE = 'polished';
const CARD_IMAGE_WIDTHS = [240, 352, 500, 800, 1200];
const CARD_IMAGE_MAX_WIDTH = 1200;

/**
 * @param {object} baseOptions
 * @param {Element} sectionRoot
 * @returns {object}
 */
export function extendMorphOptionsForCollectionCardGallery(baseOptions, sectionRoot) {
  if (!sectionRoot.querySelector('results-list')) return baseOptions;

  return {
    ...baseOptions,
    reject(oldChild, newChild) {
      if (baseOptions.reject?.(oldChild, newChild)) return true;
      return rejectCardGalleryMorph(oldChild, newChild);
    },
  };
}

/**
 * @param {Element} sectionRoot
 */
export function updateCollectionCardGalleryImages(sectionRoot) {
  if (!sectionRoot.querySelector('results-list')) return;

  const filters = parseFiltersFromUrl();
  sectionRoot.querySelectorAll('.card-gallery[data-product-id]').forEach((gallery) => {
    if (!(gallery instanceof HTMLElement)) return;
    applyFilterToGallery(gallery, filters);
  });
}

/**
 * @param {Node} oldChild
 * @param {Node} newChild
 * @returns {boolean}
 */
function rejectCardGalleryMorph(oldChild, newChild) {
  if (!(oldChild instanceof Element) || !(newChild instanceof Element)) return false;
  if (!oldChild.classList.contains('card-gallery') || !newChild.classList.contains('card-gallery')) return false;
  if (!oldChild.dataset.productId || oldChild.dataset.productId !== newChild.dataset.productId) return false;

  const newCatalog = newChild.querySelector('script[data-card-media-catalog]');
  const oldCatalog = oldChild.querySelector('script[data-card-media-catalog]');
  if (newCatalog && oldCatalog) {
    oldCatalog.textContent = newCatalog.textContent;
  } else if (newCatalog && !oldCatalog) {
    oldChild.appendChild(newCatalog.cloneNode(true));
  }

  return true;
}

/**
 * @param {URLSearchParams} [searchParams]
 * @returns {{ finish: string | null, boltLug: string | null }}
 */
function parseFiltersFromUrl(searchParams = new URLSearchParams(window.location.search)) {
  /** @type {string | null} */
  let finish = null;
  /** @type {string | null} */
  let boltLug = null;

  for (const [key, value] of searchParams.entries()) {
    if (!key.startsWith('filter.')) continue;

    const keyLower = key.toLowerCase();
    const decoded = decodeURIComponent(value.replace(/\+/g, ' '));

    if (!finish && (keyLower.includes('finish') || keyLower.includes('color'))) {
      finish = decoded;
      continue;
    }

    if (!boltLug && (keyLower.includes('bolt') || keyLower.includes('pcd'))) {
      boltLug = parseBoltLug(decoded);
    }
  }

  return { finish, boltLug };
}

/**
 * @param {string} boltPattern
 * @returns {string | null}
 */
function parseBoltLug(boltPattern) {
  const normalized = boltPattern.toLowerCase().replace(/×/g, 'x').replace(/\s/g, '');
  const lug = normalized.split('x')[0]?.trim();
  return lug || null;
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeAlt(value) {
  return value.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * @param {string} value
 * @returns {string}
 */
function handleize(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * @param {string} normalizedAlt
 * @returns {{ color: string | null, bolt: string | null, pos: number | null }}
 */
function parseAltTags(normalizedAlt) {
  const colorMatch = normalizedAlt.match(/#(?:color|finish):([^#]+)/);
  const boltMatch = normalizedAlt.match(/#bolt:(\d+)/);
  const posMatch = normalizedAlt.match(/#pos:(\d+)/);

  return {
    color: colorMatch ? colorMatch[1].trim() : null,
    bolt: boltMatch ? boltMatch[1] : null,
    pos: posMatch ? Number.parseInt(posMatch[1], 10) : null,
  };
}

/**
 * @param {Array<{ alt: string }>} catalog
 * @returns {boolean}
 */
function catalogHasPositionTags(catalog) {
  return catalog.some((media) => normalizeAlt(media.alt).includes('#pos:'));
}

/**
 * @param {Array<{ alt: string }>} catalog
 * @returns {{ normalized: string, handle: string } | null}
 */
function resolveDefaultFinish(catalog) {
  for (const media of catalog) {
    const tags = parseAltTags(normalizeAlt(media.alt));
    if (tags.color === PREFERRED_FINISH) {
      return { normalized: PREFERRED_FINISH, handle: PREFERRED_FINISH_HANDLE };
    }
  }

  for (const media of catalog) {
    const tags = parseAltTags(normalizeAlt(media.alt));
    if (tags.color === FALLBACK_FINISH) {
      return { normalized: FALLBACK_FINISH, handle: FALLBACK_FINISH_HANDLE };
    }
  }

  return null;
}

/**
 * @param {{ normalized: string, handle: string }} finish
 * @param {Array<{ alt: string }>} catalog
 * @returns {{ filterByBolt: boolean, boltLug: string | null }}
 */
function resolveBoltLug(finish, catalog, facetBoltLug) {
  const hasBoltTags = catalog.some((media) => normalizeAlt(media.alt).includes('#bolt:'));
  if (!hasBoltTags) return { filterByBolt: false, boltLug: null };

  if (facetBoltLug) {
    const hasFacetMatch = catalog.some((media) =>
      mediaMatches(media, finish.normalized, finish.handle, facetBoltLug, true)
    );
    if (hasFacetMatch) {
      return { filterByBolt: true, boltLug: facetBoltLug };
    }
  }

  for (const lug of ['6', '8', '5']) {
    const hasLug = catalog.some((media) => {
      const tags = parseAltTags(normalizeAlt(media.alt));
      return (
        tags.bolt === lug &&
        mediaMatches(media, finish.normalized, finish.handle, lug, true)
      );
    });
    if (hasLug) return { filterByBolt: true, boltLug: lug };
  }

  return { filterByBolt: false, boltLug: null };
}

/**
 * @param {{ alt: string }} media
 * @param {string} finishNormalized
 * @param {string} finishHandle
 * @param {string | null} boltLug
 * @param {boolean} filterByBolt
 * @returns {boolean}
 */
function mediaMatches(media, finishNormalized, finishHandle, boltLug, filterByBolt) {
  const normalizedAlt = normalizeAlt(media.alt);
  const tags = parseAltTags(normalizedAlt);
  const hasFinishTag = /#(?:color|finish):/.test(normalizedAlt);
  const hasBoltTag = normalizedAlt.includes('#bolt:');

  if (!hasFinishTag && !hasBoltTag) return false;

  let finishOk = true;
  if (hasFinishTag) {
    finishOk =
      tags.color === finishNormalized || (tags.color != null && handleize(tags.color) === finishHandle);
  }

  let boltOk = true;
  if (hasBoltTag) {
    if (filterByBolt && boltLug) {
      boltOk = tags.bolt === boltLug;
    }
  }

  return finishOk && boltOk;
}

/**
 * @param {Array<{ id: number, alt: string, src: string, width?: number, height?: number }>} catalog
 * @param {{ finish: string | null, boltLug: string | null }} filters
 * @returns {{ id: number, alt: string, src: string, width?: number, height?: number } | null}
 */
function pickMediaForFilters(catalog, filters) {
  if (!catalog.length || !catalogHasPositionTags(catalog)) return null;

  let finish = filters.finish
    ? { normalized: filters.finish.toLowerCase().trim(), handle: handleize(filters.finish) }
    : resolveDefaultFinish(catalog);

  if (!finish) return null;

  const { filterByBolt, boltLug } = resolveBoltLug(finish, catalog, filters.boltLug);
  /** @type {Array<{ id: number, alt: string, src: string, width?: number, height?: number }>} */
  const sorted = [];

  for (const slot of POSITION_SLOTS) {
    for (const media of catalog) {
      const normalizedAlt = normalizeAlt(media.alt);
      if (!normalizedAlt.includes(`#pos:${slot}`)) continue;
      if (!mediaMatches(media, finish.normalized, finish.handle, boltLug, filterByBolt)) continue;
      if (!sorted.some((item) => item.id === media.id)) sorted.push(media);
    }
  }

  for (const media of catalog) {
    const normalizedAlt = normalizeAlt(media.alt);
    if (/#pos:\d/.test(normalizedAlt)) continue;
    if (!mediaMatches(media, finish.normalized, finish.handle, boltLug, filterByBolt)) continue;
    if (!sorted.some((item) => item.id === media.id)) sorted.push(media);
  }

  return sorted[0] ?? null;
}

/**
 * @param {HTMLElement} gallery
 * @returns {Array<{ id: number, alt: string, src: string, width?: number, height?: number }>}
 */
function readMediaCatalog(gallery) {
  const catalogNode = gallery.querySelector('script[data-card-media-catalog]');
  if (!(catalogNode instanceof HTMLScriptElement) || !catalogNode.textContent?.trim()) return [];

  try {
    const parsed = JSON.parse(catalogNode.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
function imageUrlWithWidth(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));

    if (src.startsWith('//')) {
      return `//${url.host}${url.pathname}${url.search}`;
    }

    if (src.startsWith('/')) {
      return `${url.pathname}${url.search}`;
    }

    return `${url.pathname}${url.search}`;
  } catch {
    return src;
  }
}

/**
 * @param {string} src
 * @returns {string}
 */
function buildSrcset(src) {
  return CARD_IMAGE_WIDTHS.map((width) => `${imageUrlWithWidth(src, width)} ${width}w`).join(', ');
}

/**
 * @param {HTMLElement} gallery
 * @param {{ finish: string | null, boltLug: string | null }} filters
 */
function applyFilterToGallery(gallery, filters) {
  const catalog = readMediaCatalog(gallery);
  if (!catalog.length) return;

  const targetMedia = pickMediaForFilters(catalog, filters);
  if (!targetMedia) return;

  const sizes =
    gallery.querySelector('img.product-media__image')?.getAttribute('sizes') ||
    gallery.querySelector('img.product-media__image')?.sizes ||
    '100vw';

  if (selectExistingSlide(gallery, targetMedia, sizes)) return;

  applyMediaToVisibleImage(gallery, targetMedia, sizes);
}

/**
 * @param {HTMLElement} gallery
 * @param {{ id: number, alt: string, src: string }} targetMedia
 * @param {string} sizes
 * @returns {boolean}
 */
function selectExistingSlide(gallery, targetMedia, sizes) {
  const slide = gallery.querySelector(`slideshow-slide[slide-id="${targetMedia.id}"]`);
  if (!(slide instanceof HTMLElement)) return false;

  const slideshowComponent = gallery.querySelector('slideshow-component');
  if (slideshowComponent && typeof slideshowComponent.select === 'function') {
    slideshowComponent.select({ id: String(targetMedia.id) }, undefined, { animate: false });
  } else {
    gallery.querySelectorAll('slideshow-slide').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const isTarget = node.getAttribute('slide-id') === String(targetMedia.id);
      node.toggleAttribute('hidden', !isTarget);
      node.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
    });
  }

  const img = slide.querySelector('img.product-media__image');
  if (img instanceof HTMLImageElement) {
    ensureImageElement(img, targetMedia, sizes);
  }

  return true;
}

/**
 * @param {HTMLElement} gallery
 * @param {{ id: number, alt: string, src: string }} targetMedia
 * @param {string} sizes
 */
function applyMediaToVisibleImage(gallery, targetMedia, sizes) {
  const img =
    gallery.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
    gallery.querySelector('img.product-media__image');

  if (img instanceof HTMLImageElement) {
    ensureImageElement(img, targetMedia, sizes);
  }
}

/**
 * @param {HTMLImageElement} img
 * @param {{ alt: string, src: string }} targetMedia
 * @param {string} sizes
 */
function ensureImageElement(img, targetMedia, sizes) {
  const nextSrc = imageUrlWithWidth(targetMedia.src, CARD_IMAGE_MAX_WIDTH);
  const currentSrc = img.currentSrc || img.src;

  if (currentSrc && urlsMatch(currentSrc, nextSrc)) {
    if (img.alt !== targetMedia.alt) img.alt = targetMedia.alt;
    return;
  }

  const nextSrcset = buildSrcset(targetMedia.src);
  const preload = new Image();

  preload.onload = () => {
    img.src = nextSrc;
    img.srcset = nextSrcset;
    img.sizes = sizes;
    img.alt = targetMedia.alt;
  };

  preload.onerror = () => {
    img.src = nextSrc;
    img.srcset = nextSrcset;
    img.sizes = sizes;
    img.alt = targetMedia.alt;
  };

  preload.src = imageUrlWithWidth(targetMedia.src, 800);
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function urlsMatch(a, b) {
  try {
    const urlA = new URL(a, window.location.origin);
    const urlB = new URL(b, window.location.origin);
    return urlA.pathname === urlB.pathname && urlA.searchParams.get('width') === urlB.searchParams.get('width');
  } catch {
    return a === b;
  }
}
